import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Loader2 } from "lucide-react";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    company_name: "",
    owner_name: "",
    email: "",
    password: "",
    mobile: "",
    gst_number: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await register(form);
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    navigate("/app/dashboard", { replace: true });
  };

  return (
    <div data-testid="register-page" className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex bg-asphalt text-white relative overflow-hidden flex-col p-12">
        <div className="absolute inset-0 grid-asphalt opacity-50" />
        <Link to="/" className="relative flex items-center gap-2">
          <span className="h-8 w-8 bg-cab flex items-center justify-center rounded font-display font-black text-asphalt text-lg">W</span>
          <span className="font-display font-bold text-lg">WebCabz</span>
        </Link>
        <div className="relative mt-auto">
          <div className="text-[11px] uppercase tracking-widest text-cab font-semibold mb-3">— 14 day free trial</div>
          <h2 className="font-display font-black text-5xl tracking-tighter text-balance">
            One account. Your <span className="text-cab">entire fleet.</span>
          </h2>
          <ul className="mt-6 space-y-2 text-zinc-300 text-sm">
            <li>✓ Up to 50 vehicles on the trial</li>
            <li>✓ Bookings, drivers, GST invoices</li>
            <li>✓ Document expiry alerts</li>
            <li>✓ No credit card required</li>
          </ul>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <h1 className="font-display font-bold text-3xl text-asphalt">Create your account</h1>
          <p className="text-zinc-500 text-sm mt-1">Set up your cab company on WebCabz in 60 seconds.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Company Name" required value={form.company_name} onChange={(v) => update("company_name", v)} testid="reg-company" />
              <Field label="Owner Name" required value={form.owner_name} onChange={(v) => update("owner_name", v)} testid="reg-owner" />
            </div>
            <Field label="Work Email" type="email" required value={form.email} onChange={(v) => update("email", v)} testid="reg-email" />
            <Field label="Password" type="password" required value={form.password} onChange={(v) => update("password", v)} testid="reg-password" hint="Minimum 6 characters" />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Mobile" value={form.mobile} onChange={(v) => update("mobile", v)} testid="reg-mobile" />
              <Field label="GST Number (Optional)" value={form.gst_number} onChange={(v) => update("gst_number", v)} testid="reg-gst" />
            </div>
            {error && <div data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
            <button
              data-testid="register-submit"
              disabled={loading}
              type="submit"
              className="w-full bg-cab text-asphalt font-semibold py-2.5 rounded-md flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Start free trial <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
          <div className="mt-6 text-sm text-zinc-600">
            Already have an account?{" "}
            <Link to="/login" data-testid="link-to-login" className="text-asphalt font-semibold hover:text-cab">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testid, hint }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">{label}{required && " *"}</label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2.5 rounded-md border border-zinc-300 focus:ring-2 focus:ring-cab focus:border-cab outline-none text-sm"
      />
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}
